import {Checkbox, CheckboxProps, createStyles, Flex} from '@mantine/core';
import React, {RefObject, useEffect, useRef} from 'react';

import HelpButton from 'help/HelpButton';
import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import {FloatingInteraction} from 'ui-library/floating-ui/FloatingConstants';
import {LabelStyle} from 'ui-library/inputs/InputConstants';
import {InputChangeEvent} from 'util/Types';

export interface CheckboxInputProps {
  checked: boolean;
  className?: string;
  // id is required for now due to the htmlFor implementation detail, but in the future we could wrap the
  // and text into a <label> to avoid the need for that.
  id: string;
  indeterminate?: boolean;
  inputRef?: RefObject<HTMLInputElement>;
  label: string | JSX.Element;
  labelStyle?: LabelStyle;
  helpText?: string | JSX.Element;
  disabled?: boolean;
  onChange: (e: InputChangeEvent) => void;
  dataTestAttrs?: DataTestAttrs;
}

const useStyles = createStyles(() => ({
  checkboxLabel: {
    // match old spacing
    paddingLeft: 6,
    lineHeight: '16px',
    // override benchling.css styles
    display: 'unset',
    marginBottom: 'unset',
    fontWeight: 'normal',
  },
  checkboxInner: {
    // center box with larger line height
    marginTop: 2,
  },
  helpButton: {
    verticalAlign: 'text-top',
  },
}));

/**
 * @deprecated Use Mantine's `Checkbox` directly instead.
 */
function CheckboxInput({
  checked,
  className,
  id,
  indeterminate,
  inputRef,
  label,
  labelStyle,
  helpText,
  disabled = false,
  onChange,
  dataTestAttrs,
}: CheckboxInputProps): JSX.Element {
  const {classes, cx} = useStyles();

  // You can't conditionally use hooks, so we always do a useRef and just don't use it if we're passed a
  // ref in props.
  const internalRef = useRef<HTMLInputElement | null>(null);
  const ref = inputRef ?? internalRef;
  useEffect(() => {
    if (ref.current != null && indeterminate != null) {
      ref.current.indeterminate = indeterminate;
    }
  }, [ref, indeterminate]);

  const labelProps: Partial<CheckboxProps> = {};
  if (labelStyle === LabelStyle.HIDDEN) {
    labelProps['aria-label'] = label as string;
  } else if (labelStyle === LabelStyle.LABEL || !labelStyle) {
    labelProps.label = label;
  } else if (labelStyle === LabelStyle.SUBLABEL) {
    labelProps.description = label;
  }

  return (
    <Flex className={className} {...getTestProps({component: 'CheckboxInput', element: 'root'})}>
      <Checkbox
        size="14px"
        classNames={{label: classes.checkboxLabel, inner: classes.checkboxInner}}
        checked={checked}
        indeterminate={indeterminate}
        id={id}
        onChange={onChange}
        disabled={disabled}
        ref={ref}
        {...labelProps}
        {...getTestProps(dataTestAttrs)}
      />
      {helpText && (
        <HelpButton
          renderContent={() => <p>{helpText}</p>}
          iconClassName={cx('u-mini-spacing-left', classes.helpButton)}
          interaction={FloatingInteraction.HOVER}
        />
      )}
    </Flex>
  );
}

export default CheckboxInput;
